/*
Copyright© Celligence International, LLC - 2024
This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { Button, FormControl, TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { NumericFormat } from 'react-number-format';
import Styles from '../css/AmortizationSchedulePage.module.css';
import { useAuthContext } from '../contexts/AuthContext';
import { useViewingContext } from '../contexts/ViewingContext';
import { useAmortizationScheduleContext } from '../contexts/AmortizationScheduleContext';

const AmortizationScenarioCol = (scenarioId) => {
  const { fullAccess } = useAuthContext();
  const { isServicerView } = useViewingContext();
  const amortizationScheduleContext = useAmortizationScheduleContext();
  const [scenario, scenarioPayment] = amortizationScheduleContext.getScenarioById(scenarioId.scenarioId);
  const [setScenario, setScenarioPayment] = amortizationScheduleContext.getScenarioSettersById(scenarioId.scenarioId);

  return (
    <div className={Styles.rightColumn}>
      <table className={Styles.amortTableSection}>
        <tbody>
          <tr className={Styles.amortTableHeaderRow}>
            <td>
              Scenario {scenario.scenarioId.charAt(1)}
              <Button
                className={Styles.primaryButtonSmall}
                disabled={!fullAccess && isServicerView}
                onClick={() => amortizationScheduleContext.deleteAmortizationScenario(scenarioId.scenarioId)}
              >
                X
              </Button>
            </td>
          </tr>
          <tr>
            <td>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <FormControl>
                  <DatePicker
                    label='Due Date'
                    value={scenario.dueDate}
                    shouldDisableDate={(date) =>
                      date.get('date') !== 1 || date.isBefore(amortizationScheduleContext.firstPaymentDayjs, 'day')
                    }
                    className={Styles.smallTextField}
                    onChange={(newDate) => {
                      setScenario({
                        loanNumber: scenario.loanNumber,
                        servicerLoan: scenario.servicerLoan,
                        scenarioId: scenario.scenarioId,
                        loanAmount: scenario.loanAmount,
                        loanTerm: scenario.loanTerm,
                        lpi: scenario.lpi,
                        dueDate: newDate,
                      });
                      setScenarioPayment({
                        isLoading: false,
                        upb: scenarioPayment.upb,
                        remainingTerm: scenarioPayment.remainingTerm,
                        intRate: scenarioPayment.intRate,
                        apparPi: scenarioPayment.apparPi,
                        prinCurtail: scenarioPayment.prinCurtail,
                        dueDate: newDate.unix(),
                        paidOff: scenarioPayment.paidOff,
                      });
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        required: true,
                      },
                    }}
                  />
                </FormControl>
              </LocalizationProvider>
            </td>
          </tr>
          <tr>
            <td>
              <NumericFormat
                fullWidth
                size='small'
                placeholder='Loan Amount'
                label='Loan Amount'
                value={scenario.loanAmount}
                error={scenario.loanAmount <= 0}
                customInput={TextField}
                thousandSeparator={true}
                prefix={'$'}
                decimalScale={2}
                fixedDecimalScale={true}
                allowNegative={false}
                className={Styles.smallTextField}
                onValueChange={(values) => {
                  amortizationScheduleContext.updatePI(
                    scenarioId.scenarioId,
                    values.floatValue,
                    scenario.loanTerm,
                    scenarioPayment.intRate,
                  );
                }}
              />
            </td>
          </tr>
          <tr>
            <td>
              <NumericFormat
                fullWidth
                size='small'
                placeholder='Loan Term'
                label='Loan Term'
                value={scenario.loanTerm}
                error={scenario.loanTerm <= 0}
                customInput={TextField}
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
                className={Styles.smallTextField}
                onValueChange={(values) => {
                  amortizationScheduleContext.updatePI(
                    scenarioId.scenarioId,
                    scenario.loanAmount,
                    values.floatValue,
                    scenarioPayment.intRate,
                  );
                }}
              />
            </td>
          </tr>
          <tr>
            <td>
              <NumericFormat
                fullWidth
                size='small'
                placeholder='Principal Balance'
                label='Principal Balance'
                value={scenarioPayment.upb}
                error={scenarioPayment.upb <= 0}
                customInput={TextField}
                thousandSeparator={true}
                prefix={'$'}
                decimalScale={2}
                fixedDecimalScale={true}
                allowNegative={false}
                className={Styles.smallTextField}
                onValueChange={(values) => {
                  setScenarioPayment({
                    isLoading: false,
                    upb: values.floatValue,
                    remainingTerm: scenarioPayment.remainingTerm,
                    intRate: scenarioPayment.intRate,
                    apparPi: scenarioPayment.apparPi,
                    prinCurtail: scenarioPayment.prinCurtail,
                    dueDate: scenarioPayment.dueDate,
                    paidOff: scenarioPayment.paidOff,
                  });
                }}
              />
            </td>
          </tr>
          <tr>
            <td>
              <NumericFormat
                fullWidth
                size='small'
                placeholder='Remaining Term'
                label='Remaining Term'
                value={scenarioPayment.remainingTerm}
                error={scenarioPayment.remainingTerm <= 0}
                customInput={TextField}
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
                className={Styles.smallTextField}
                onValueChange={(values) => {
                  setScenarioPayment({
                    isLoading: false,
                    upb: scenarioPayment.upb,
                    remainingTerm: values.floatValue,
                    intRate: scenarioPayment.intRate,
                    apparPi: scenarioPayment.apparPi,
                    prinCurtail: scenarioPayment.prinCurtail,
                    dueDate: scenarioPayment.dueDate,
                    paidOff: scenarioPayment.paidOff,
                  });
                }}
              />
            </td>
          </tr>
          <tr>
            <td>
              <NumericFormat
                fullWidth
                size='small'
                placeholder='Interest Rate'
                label='Interest Rate'
                value={scenarioPayment.intRate}
                error={scenarioPayment.intRate <= 0}
                customInput={TextField}
                thousandSeparator={true}
                suffix={'%'}
                decimalScale={3}
                fixedDecimalScale={true}
                allowNegative={false}
                className={Styles.smallTextField}
                onValueChange={(values) => {
                  amortizationScheduleContext.updatePI(
                    scenarioId.scenarioId,
                    scenario.loanAmount,
                    scenario.loanTerm,
                    values.floatValue,
                  );
                }}
              />
            </td>
          </tr>
          <tr>
            <td>
              <NumericFormat
                fullWidth
                size='small'
                placeholder='P & I Payment'
                label='P & I Payment'
                value={scenarioPayment.apparPi}
                error={scenarioPayment.apparPi <= 0}
                customInput={TextField}
                thousandSeparator={true}
                prefix={'$'}
                decimalScale={2}
                fixedDecimalScale={true}
                allowNegative={false}
                className={Styles.smallTextField}
                disabled
              />
            </td>
          </tr>
          <tr>
            <td>
              <NumericFormat
                fullWidth
                size='small'
                placeholder='Additional Principal'
                label='Additional Principal'
                value={scenarioPayment.prinCurtail}
                error={scenarioPayment.prinCurtail < 0}
                customInput={TextField}
                thousandSeparator={true}
                prefix={'$'}
                decimalScale={2}
                fixedDecimalScale={true}
                allowNegative={false}
                className={Styles.smallTextField}
                onValueChange={(values) => {
                  setScenarioPayment({
                    isLoading: false,
                    upb: scenarioPayment.upb,
                    remainingTerm: scenarioPayment.remainingTerm,
                    intRate: scenarioPayment.intRate,
                    apparPi: scenarioPayment.apparPi,
                    prinCurtail: values.floatValue,
                    dueDate: scenarioPayment.dueDate,
                    paidOff: scenarioPayment.paidOff,
                  });
                }}
              />
            </td>
          </tr>
          <tr>
            <td className={Styles.tdButton}>
              <Button
                className={Styles.primaryButton}
                disabled={!fullAccess && isServicerView}
                onClick={() => amortizationScheduleContext.printAmortizationSchedule(scenario.loanNumber)}
              >
                Save & Print
              </Button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default AmortizationScenarioCol;
